// Library Methods
import { useQueries, useQuery } from "@tanstack/react-query";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import {
  pollUnlessLive,
  useIsEMRLive,
} from "src/services/emr/live/emrLiveStatus";

// Types and API
import { EMRProblemType } from "../../types";
import { getEMRHubProblems } from "../../api";

export const HUB_PROBLEMS_REFRESH_MS = 60_000;

const hubProblemsQuery = (
  clinicId: string | undefined,
  hubId: string | undefined,
  getToken: () => Promise<string>,
  isLive: boolean
) => ({
  queryKey: ["emr-hub-problems", clinicId, hubId],
  queryFn: async (): Promise<Array<EMRProblemType>> => {
    if (!clinicId || !hubId) throw new Error();
    const token = await getToken();
    return getEMRHubProblems(token, clinicId, hubId);
  },
  enabled: Boolean(clinicId && hubId),
  refetchInterval: pollUnlessLive(isLive, HUB_PROBLEMS_REFRESH_MS),
  refetchOnWindowFocus: true,
  staleTime: 0,
});

export const useEMRHubProblems = (
  clinicId: string | undefined,
  hubId: string | undefined
) => {
  const { getToken } = useTokenAccess();
  const isLive = useIsEMRLive();

  return useQuery(hubProblemsQuery(clinicId, hubId, getToken, isLive));
};

export const useEMRProblemsAcrossHubs = (
  clinicId: string | undefined,
  hubIds: Array<string>
) => {
  const { getToken } = useTokenAccess();
  const isLive = useIsEMRLive();

  return useQueries({
    queries: hubIds.map((hubId) =>
      hubProblemsQuery(clinicId, hubId, getToken, isLive)
    ),
    combine: (results) =>
      results.flatMap((result) =>
        Array.isArray(result.data) ? result.data : []
      ),
  });
};
